<template>
  <div
    v-show="visible"
    class="layer_panel"
    data-guide="layer-panel"
    :class="{ collapsed: !expanded, dragging: isDragging }"
    :style="{ left: `${position.x}px`, top: `${position.y}px` }"
    @touchstart.stop="handleDragStart"
    @touchmove.stop.prevent="handleDragMove"
    @touchend.stop="handleDragEnd"
    @mousedown.stop="handleDragStart"
    @click.capture="handlePanelClick"
  >
    <div
      class="layer_panel_header"
    >
      <div class="layer_panel_title">
        <span>图层</span>
        <em>{{ layers.length }}</em>
      </div>
      <button
        v-if="!expanded"
        class="layer_panel_collapsed_label"
        type="button"
        @click.stop="toggleExpanded"
      >
        图层
      </button>
      <div v-else class="layer_panel_controls">
        <button
          type="button"
          class="layer_panel_button"
          :class="{ active: allVisible }"
          :title="allVisible ? '全部隐藏' : '全部显示'"
          @click.stop="toggleAllVisible"
        >
          <van-icon :name="allVisible ? 'eye-o' : 'closed-eye'" />
        </button>
        <button class="layer_panel_button" type="button" @click.stop="toggleExpanded">
          <van-icon :name="expanded ? 'arrow-up' : 'arrow-down'" />
        </button>
      </div>
    </div>
    <div v-show="expanded" class="layer_panel_body">
      <div v-if="displayLayers.length" ref="layerList" class="layer_list">
        <div
          v-for="(item, index) in displayLayers"
          :key="item.id"
          :ref="`layerItem-${item.id}`"
          class="layer_item"
          :data-guide="index === 0 ? 'layer-first-item' : null"
          :class="{
            selected: item.selected,
            hidden: !item.visible,
            container: item.type === 'container',
          }"
          @click="selectLayer(item)"
        >
          <div class="layer_item_main">
            <button
              type="button"
              class="layer_visibility_button"
              :class="{ active: item.visible }"
              :title="item.visible ? '隐藏' : '显示'"
              :data-guide="index === 0 ? 'layer-first-eye' : null"
              @click.stop="toggleVisible(item)"
            >
              <van-icon :name="item.visible ? 'eye-o' : 'closed-eye'" />
            </button>
            <span v-if="item.type === 'container'" class="layer_type_badge">器</span>
            <div class="layer_item_name">{{ item.name }}</div>
          </div>
        </div>
      </div>
      <div v-else class="layer_empty">还没有图层</div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      expanded: true,
      visible: true,
      layers: [],
      position: {
        x: 0,
        y: 110,
      },
      dragStart: {
        x: 0,
        y: 0,
        left: 0,
        top: 0,
      },
      isDragging: false,
      movedDuringDrag: false,
      mouseMoveHandler: null,
      mouseUpHandler: null,
    };
  },
  computed: {
    allVisible() {
      return this.layers.length > 0 && this.layers.every((item) => item.visible);
    },
    displayLayers() {
      return this.layers
        .slice()
        .reverse()
        .map((item) => ({ ...item }));
    },
  },
  created() {
    this.$bus.$on("layer-change", (layers) => {
      this.layers = layers || [];
      this.$nextTick(() => {
        this.scrollToSelectedLayer();
      });
    });
    this.$bus.$on("layer-panel-toggle", () => {
      this.visible = !this.visible;
      this.emitVisibleChange();
    });
    this.$bus.$on("layer-panel-show", () => {
      this.visible = true;
      this.emitVisibleChange();
    });
    this.$bus.$on("layer-panel-hide", () => {
      this.visible = false;
      this.emitVisibleChange();
    });
  },
  mounted() {
    this.resetPosition();
    this.emitVisibleChange();
  },
  beforeDestroy() {
    if (this.mouseMoveHandler) {
      window.removeEventListener("mousemove", this.mouseMoveHandler);
    }
    if (this.mouseUpHandler) {
      window.removeEventListener("mouseup", this.mouseUpHandler);
    }
  },
  methods: {
    designPx(value) {
      return (Math.min(window.innerWidth, 750) / 750) * value;
    },
    resetPosition() {
      this.position.x = 5;
      this.position.y = this.designPx(360);
      this.clampPosition(this.position.x, this.position.y);
    },
    toggleExpanded() {
      this.expanded = !this.expanded;
      this.$nextTick(() => {
        this.position.x = Math.min(
          this.position.x,
          window.innerWidth - this.panelWidth() - 8
        );
      });
    },
    emitVisibleChange() {
      this.$bus.$emit("layer-panel-visible-change", this.visible);
    },
    panelWidth() {
      return this.expanded ? 108 : 52;
    },
    panelHeight() {
      return this.expanded ? Math.min(window.innerHeight * 0.5, 360) : 42;
    },
    clampPosition(nextX, nextY) {
      const minOffset = 5;
      const maxX = Math.max(window.innerWidth - this.panelWidth() - minOffset, minOffset);
      const maxY = Math.max(window.innerHeight - this.panelHeight() - minOffset, minOffset);
      this.position.x = Math.min(Math.max(minOffset, nextX), maxX);
      this.position.y = Math.min(Math.max(minOffset, nextY), maxY);
    },
    getPoint(event) {
      if (event.touches && event.touches.length) {
        return event.touches[0];
      }
      return event;
    },
    handlePanelClick(event) {
      if (!this.movedDuringDrag) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    },
    handleDragStart(event) {
      if (event.button !== undefined && event.button !== 0) {
        return;
      }
      const point = this.getPoint(event);
      this.isDragging = true;
      this.movedDuringDrag = false;
      this.dragStart = {
        x: point.clientX,
        y: point.clientY,
        left: this.position.x,
        top: this.position.y,
      };
      if (!event.touches) {
        this.mouseMoveHandler = (moveEvent) => this.handleDragMove(moveEvent);
        this.mouseUpHandler = () => this.handleDragEnd();
        window.addEventListener("mousemove", this.mouseMoveHandler);
        window.addEventListener("mouseup", this.mouseUpHandler, { once: true });
      }
    },
    handleDragMove(event) {
      if (!this.isDragging) {
        return;
      }
      const point = this.getPoint(event);
      const deltaX = point.clientX - this.dragStart.x;
      const deltaY = point.clientY - this.dragStart.y;
      if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) {
        this.movedDuringDrag = true;
      }
      this.clampPosition(
        this.dragStart.left + deltaX,
        this.dragStart.top + deltaY
      );
    },
    handleDragEnd() {
      this.isDragging = false;
      if (this.mouseMoveHandler) {
        window.removeEventListener("mousemove", this.mouseMoveHandler);
        this.mouseMoveHandler = null;
      }
      this.mouseUpHandler = null;
      window.setTimeout(() => {
        this.movedDuringDrag = false;
      }, 0);
    },
    selectLayer(item) {
      this.$bus.$emit("guide-control-change", {
        key: "layer",
      });
      this.$bus.$emit("layer-select", item.id);
    },
    setAllVisible(visible) {
      this.$bus.$emit("guide-control-change", {
        key: "layer",
      });
      this.$bus.$emit("layer-set-all-visible", visible);
    },
    toggleAllVisible() {
      this.setAllVisible(!this.allVisible);
    },
    toggleVisible(item) {
      this.$bus.$emit("guide-control-change", {
        key: "layer",
      });
      this.$bus.$emit("layer-toggle-visible", item.id);
    },
    scrollToSelectedLayer() {
      const selectedLayer = this.displayLayers.find((item) => item.selected);
      if (!selectedLayer || !this.$refs.layerList) {
        return;
      }
      const targetRef = this.$refs[`layerItem-${selectedLayer.id}`];
      const target = Array.isArray(targetRef) ? targetRef[0] : targetRef;
      if (!target || !target.scrollIntoView) {
        return;
      }
      target.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.layer_panel {
  position: fixed;
  z-index: 40;
  width: 108px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.62);
  box-shadow: 0 rem(6) rem(18) rgba(34, 52, 17, 0.12);
  backdrop-filter: blur(6px);
  overflow: hidden;
  transition: width 0.2s ease, opacity 0.2s ease;
}

.layer_panel.collapsed {
  width: 52px;
}

.layer_panel.dragging {
  opacity: 0.82;
}

.layer_panel_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 30px;
  padding: 5px 6px;
  background: rgba(110, 159, 33, 0.82);
  color: #fff;
  cursor: move;
  box-sizing: border-box;
}

.layer_panel_title {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
}

.layer_panel_title em {
  display: inline-flex;
  min-width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  font-style: normal;
  font-size: 10px;
}

.layer_panel_controls {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
}

.layer_panel_button {
  border: 0;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  border-radius: 999px;
  width: 20px;
  height: 20px;
  padding: 0;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.layer_panel_button.active {
  background: rgba(255, 255, 255, 0.32);
}

.layer_panel_collapsed_label {
  border: 0;
  background: transparent;
  color: #fff;
  padding: 0;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;
}

.layer_panel_body {
  padding: 5px;
}

.layer_visibility_button {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 999px;
  background: rgba(237, 243, 223, 0.78);
  color: #4d6720;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
}

.layer_visibility_button.active {
  background: rgba(223, 236, 196, 0.88);
  color: #5b861d;
}

.layer_list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: calc(100vh - #{rem(360)});
  overflow-y: auto;
}

.layer_item {
  border-radius: 7px;
  border: 1px solid rgba(120, 150, 62, 0.12);
  background: rgba(249, 251, 244, 0.62);
  padding: 4px;
}

.layer_item.selected {
  border-color: #5d9a16;
  box-shadow:
    inset 0 0 0 2px rgba(93, 154, 22, 0.68),
    0 0 0 1px rgba(255, 255, 255, 0.86);
  background: rgba(225, 244, 190, 0.9);
}

.layer_item.selected .layer_item_name {
  color: #244b0b;
  font-weight: 700;
}

.layer_item.container {
  background: rgba(247, 250, 241, 0.72);
}

.layer_item.hidden {
  opacity: 0.55;
}

.layer_item_main {
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 26px;
}

.layer_item_name {
  flex: 1;
  min-width: 0;
  color: #344614;
  font-size: 11px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.layer_type_badge {
  flex: 0 0 auto;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 999px;
  background: rgba(110, 159, 33, 0.14);
  color: #4b7418;
  font-size: 10px;
  line-height: 18px;
  text-align: center;
  font-weight: 700;
  box-sizing: border-box;
}

.layer_empty {
  padding: 12px 5px;
  text-align: center;
  color: #7d876d;
  font-size: 12px;
}

.layer_panel.collapsed .layer_panel_header {
  justify-content: center;
  padding: 5px;
}

.layer_panel.collapsed .layer_panel_title {
  display: none;
}
</style>
